import React, { FC, memo, useEffect, useState, PropsWithChildren } from "react";
import { useSelector } from "react-redux";
import Router from "next/router";
import getConfig from "next/config";

import { Preview } from "components/Preview";
import { authenticationStatusUpdated, pageView } from "lib/analytics";
import { useMediaContext } from "lib/media-query";
import { Auth } from "services/auth";
import { withToast } from "components/shared/Notifications/ToastProvider";
import { MOBILE_MENU_OPEN } from "helpers";
import { useCookies } from "lib/cookie-monster";
import { StructuredData } from "components/StructuredData";
import { RootState } from "store/types";
import { Navigation } from "components/Navigation";
import Styles from "./styles.scss";
import ThirdPartyScripts from "./elements/ThirdPartyScripts";
import { MetaTags } from "./elements/MetaTags";
import { MobileAppPrompt } from "./elements/MobileAppPrompt";

const { publicRuntimeConfig = {} } = getConfig() || {};

interface BaseLayoutProps {
    title?: string;
    canonicalUrl?: string;
    description?: string;
    jumbotronPage?: boolean;
    metaDescription?: string;
    pageName?: string;
    pageType?: string; // TODO: should this be required?
    socialMediaImage?: string;
    heroImage?: string;
    googleSearchConsole?: string;
    faceBookPixel?: string;
    googleAnalyticsId?: string;
}

const BaseLayoutComp: FC<PropsWithChildren<BaseLayoutProps>> = ({
    title = "AutoTrack | De makkelijkste weg naar je volgende auto",
    jumbotronPage = false,
    pageName = "pagename-unknown",
    description = "Beter geïnformeerd de juiste auto kopen? Bekijk ons ruime aanbod en lees onze tips. Ontdek de voordelen en het gemak van AutoTrack.",
    metaDescription,
    socialMediaImage = `/static/images/logo-autotrack.svg?version=${process.env.VERSION}`,
    canonicalUrl = "https://www.autotrack.nl/",
    children,
    pageType = "undefined",
    heroImage,
    googleSearchConsole,
    faceBookPixel
}) => {
    // gives an error for the page snapshots without the events check
    Router.events?.on("beforeHistoryChange", () => {
        document.body.classList.remove(MOBILE_MENU_OPEN);
    });

    const [mouseDown, setMouseDown] = useState(false);
    const { cookies } = useCookies();

    const user = useSelector<RootState, Auth>(({ authentication }) => authentication);

    useEffect(() => {
        if (process.browser && cookies.disableUsabilla !== "on") {
            const checkUsabillaAvailabilityInterval = setInterval(() => {
                if (window.usabilla_live) {
                    clearInterval(checkUsabillaAvailabilityInterval);
                    const clientIdCookie = cookies._ga;
                    window.usabilla_live("data", {
                        custom: {
                            clientIdCookie
                        }
                    });
                }
                return () => clearInterval(checkUsabillaAvailabilityInterval);
            }, 100);
        }
    }, []);

    const onMainMouseDown = () => {
        setMouseDown(true);
    };

    const onGlobalMouseUp = () => {
        if (mouseDown) {
            document.body.classList.remove(MOBILE_MENU_OPEN);
        }
        setMouseDown(false);
    };

    const _toggleMenu = () => {
        if (document.body.classList.contains(MOBILE_MENU_OPEN)) {
            document.body.classList.remove(MOBILE_MENU_OPEN);
        } else {
            document.body.classList.add(MOBILE_MENU_OPEN);
        }
    };

    const { href = "" } = (typeof document !== "undefined" ? document : { location: {} }).location;
    const { viewport, device, orientation } = useMediaContext();

    useEffect(() => {
        if (href === "") {
            return;
        }
        const { userAgent, language } = navigator;
        const url = new URL(href);
        pageView({
            host: url.hostname,
            path: url.pathname,
            query: url.search,
            browserLanguage: language,
            envName: publicRuntimeConfig.PUBLIC_ENV_NAME,
            title,
            userAgent,
            pageType,
            viewport,
            device,
            orientation
        });
        authenticationStatusUpdated(user, cookies);
    }, [href]);

    return (
        <>
            <div
                className="app"
                onMouseUp={onGlobalMouseUp}
                onTouchEnd={onGlobalMouseUp}
                role="presentation"
            >
                <MetaTags
                    metaDescription={metaDescription}
                    description={description}
                    title={title}
                    socialMediaImage={socialMediaImage}
                    canonicalUrl={canonicalUrl}
                    heroImage={heroImage}
                    googleSearchConsole={googleSearchConsole}
                    faceBookPixel={faceBookPixel}
                />
                <style jsx>{Styles}</style>
                <ThirdPartyScripts pageName={pageName} />
                <Navigation />
                <StructuredData type="Organization" />
                <Preview />
                <main
                    className={jumbotronPage ? "jumbotron--page" : ""}
                    role="presentation"
                    onMouseDown={onMainMouseDown}
                    onTouchStart={onMainMouseDown}
                >
                    {children}
                    {!["autodetail-overzicht", "media"].includes(pageName) && <MobileAppPrompt />}
                </main>
            </div>
        </>
    );
};

export const BaseLayout = memo(withToast(BaseLayoutComp));

BaseLayout.displayName = "BaseLayout";
